import { SafetyDivider } from "@mui/icons-material";
import { startCase } from "lodash";
import { FC } from "react";
import { FormProvider, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  DirectionEnum,
  IRideDto,
  RideFormFields,
  SINGLE_KEY,
  useHaveCustomPermissions,
} from "@ryde/shared";

import { MuiButton } from "../../../../controls";
import { DropdownTemplate, NumberTemplate } from "../../../../dynamic-forms";
import { TableLoadingIndicator } from "../../../../grid";
import { FramerTooltip } from "../../../tooltips";
import {
  ButtonAddEntity,
  ButtonAddPassenger,
  ButtonOpenPassenger,
  ButtonOpenStation,
} from "../controls";
import { AddPassengerFormNames } from "./ride-add-passenger.models";
import { RideAddPassengerFormStyled } from "./ride-add-passenger.styled";
import { RideAddPassengerViewSwitch } from "./ride-add-passenger.view-switch";
import { useAddPassenger } from "./use-add-passenger";

interface IRideAddStation {
  rideDto: IRideDto;
  closeExpanded: () => void;
}

export const RideAddPassenger: FC<IRideAddStation> = ({
  rideDto,
  closeExpanded,
}) => {
  const direction = useWatch({ name: RideFormFields.direction });
  const { t } = useTranslation();

  const {
    methods,
    createNewPassenger,
    openPassengerCard,
    stationsFromDropdownList,
    stationsToDropdownList,
    passengerToStationAddHandler,
    passengerDropdownListFiltered,
    openStationFromCard,
    openStationToCard,
    createNewStationTo,
    createNewStationFrom,
    boldStationsFromIds,
    boldStationsToIds,
    optionIconMapping,
    stationFromWatchedId,
    stationToWatchedId,
    passengerWatchedId,
    stationsDisplayOptions,
    isPassengersDataLoading,
    isLimitedPermissionsPassenger,
  } = useAddPassenger({ rideDto, direction });

  const {
    hasPermissionEditStation,
    hasPermissionEditPassenger,
    hasPermissionCreatePassenger,
    hasPermissionCreateStation,
    canOpenPassengerCard,
    canOpenStationCard,
  } = useHaveCustomPermissions();

  if (isPassengersDataLoading) {
    return <TableLoadingIndicator loading={isPassengersDataLoading} />;
  }

  const isOpenStationButtonVisible =
    canOpenStationCard && !isLimitedPermissionsPassenger;

  return (
    <FormProvider {...methods}>
      <RideAddPassengerFormStyled>
        <div className="passenger_container">
          <DropdownTemplate
            name={AddPassengerFormNames.passengerAddId}
            size="small"
            formLabel={startCase(t(`passenger.${SINGLE_KEY}`))}
            formAttributes={{
              selectItems: passengerDropdownListFiltered,
              disabled: !direction,
              skipDropdownItemsTranslation: true,
            }}
          />
          <NumberTemplate
            name={AddPassengerFormNames.seats}
            size="small"
            formAttributes={{ disabled: !direction }}
            className="ride-add-passenger__seats"
          />
          {canOpenPassengerCard && !isLimitedPermissionsPassenger && (
            <ButtonOpenPassenger
              watchedId={passengerWatchedId}
              onClick={openPassengerCard}
              readonlyMode={!hasPermissionEditPassenger}
            />
          )}

          {!!isLimitedPermissionsPassenger && (
            <FramerTooltip content={t("ride.passengerFromOtherSite")}>
              <SafetyDivider className="passenger-from-other-site__icon" />
            </FramerTooltip>
          )}

          {/* to avoid breaking grid layout */}
          {!canOpenPassengerCard && !isLimitedPermissionsPassenger && (
            <span></span>
          )}
          {hasPermissionCreatePassenger && (
            <ButtonAddEntity
              tooltip={t("ride.createPassengerStationPanel")}
              onClick={createNewPassenger}
            />
          )}
          {/* to avoid breaking grid layout */}
          {!hasPermissionCreatePassenger && <span></span>}
        </div>
        <div className="stations_container">
          <DropdownTemplate
            name={AddPassengerFormNames.stationFromId}
            formLabel={t("ride.formLabel.stationFromId")}
            size="small"
            formAttributes={{
              selectItems: stationsFromDropdownList,
              boldValues: boldStationsFromIds,
              optionIconMapping:
                direction === DirectionEnum.PickUp
                  ? optionIconMapping
                  : undefined,
              disabled: !direction,
              skipDropdownItemsTranslation: true,
            }}
          />
          {isOpenStationButtonVisible && (
            <ButtonOpenStation
              watchedId={stationFromWatchedId}
              onClick={openStationFromCard}
              readonlyMode={!hasPermissionEditStation}
              tooltipLabelTranslationKey={t("ride.MANAGE_STATION", {
                stationType: t("ride.formLabel.stationFromId"),
              })}
            />
          )}
          {/* to avoid breaking grid layout */}
          {!isOpenStationButtonVisible && <span></span>}
          {hasPermissionCreateStation && (
            <ButtonAddEntity
              tooltip={t("ride.createStationStationPanel")}
              onClick={createNewStationFrom}
            />
          )}
          {/* to avoid breaking grid layout */}
          {!hasPermissionCreateStation && <span></span>}

          <DropdownTemplate
            name={AddPassengerFormNames.stationToId}
            formLabel={t("ride.formLabel.stationToId")}
            size="small"
            formAttributes={{
              selectItems: stationsToDropdownList,
              boldValues: boldStationsToIds,
              optionIconMapping:
                direction === DirectionEnum.DropOff
                  ? optionIconMapping
                  : undefined,
              disabled: !direction,
              skipDropdownItemsTranslation: true,
            }}
          />
          {canOpenStationCard && (
            <ButtonOpenStation
              watchedId={stationToWatchedId}
              onClick={openStationToCard}
              readonlyMode={!hasPermissionEditStation}
              tooltipLabelTranslationKey={t("ride.MANAGE_STATION", {
                stationType: t("ride.formLabel.stationToId"),
              })}
            />
          )}
          {/* to avoid breaking grid layout */}
          {!canOpenStationCard && <span></span>}
          {hasPermissionCreateStation && (
            <ButtonAddEntity
              tooltip={t("ride.createStationStationPanel")}
              onClick={createNewStationTo}
            />
          )}
          {/* to avoid breaking grid layout */}
          {!hasPermissionCreateStation && <span></span>}
        </div>
        {stationsDisplayOptions.length > 1 && (
          <RideAddPassengerViewSwitch
            name="viewStationsOption"
            stationsDisplayOptions={stationsDisplayOptions}
          />
        )}
        <div className="add-passengers__form-actions">
          <MuiButton onClick={closeExpanded} muiVariant="text">
            {t("general.CANCEL")}
          </MuiButton>

          <ButtonAddPassenger
            watchedId={stationToWatchedId}
            stationsChosen={!!stationFromWatchedId && !!stationToWatchedId}
            onClick={passengerToStationAddHandler}
            direction={direction}
          />
        </div>
      </RideAddPassengerFormStyled>
    </FormProvider>
  );
};
